import React, { useState, useContext, useEffect } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { uppercaseFirst } from "../../utils/strings";
import usePromise from "react-use-promise";
import { CurrencySymbol } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS-ADJACENT (read-only payment ledger). Styling-only: no handler,
 * condition, state value, prop or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer`, `myAccountTitle`, `bodyText`, `bodyTextSml` and
 * `historyContainer` were declared but never referenced from the JSX, so they
 * rendered nothing. The `<Grid container className={classes.cardContainer}>`
 * referenced a key that does not exist in this file's `useStyles` object, so
 * it resolved to `className={undefined}` and applied nothing — that container
 * therefore takes no `sx`.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

export default function PaymentHistory() {
  // Account Context
  const { getMembershipDetails, resetErrors } = useContext(AccountContext);

  useEffect(() => {
    return () => resetErrors();
    // eslint-disable-next-line
  }, []);

  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);
  const loading = state === "pending";

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  return (
    <Grid container sx={v4GridContainerWidthSx}>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>Payment History</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          Payment History
        </Typography>
      </Grid>
      <Grid container sx={v4GridContainerWidthSx}>
        {loading && (
          <Grid size={{ xs: 12, sm: 12 }}>
            <Box sx={{ m: 5 }} align="center">
              <CircularProgress variant="indeterminate"></CircularProgress>
            </Box>
          </Grid>
        )}
        {!loading && (
          <>
            {membershipDetails && (
              <>
                <TableContainer elevation={0}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Amount</TableCell>
                        <TableCell align="left">Date</TableCell>
                        <TableCell align="left">Method</TableCell>
                        <TableCell align="left">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <>
                      <TableBody>
                        {membershipDetails.payments.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {item.cost <= 0 && "N/A"}
                              {item.cost > 0 && (
                                <>
                                  {CurrencySymbol({
                                    currency: item.currency,
                                  })}
                                  {item.cost}
                                </>
                              )}
                              {/* {parseFloat(item.cost) <= 0
                                      ? "N/A"
                                      : "£" + parseFloat(item.cost).toFixed(2)}
                                       */}
                            </TableCell>
                            <TableCell>
                              {new Date(item.created).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {item.paymentProvider === "stripe"
                                ? "Card"
                                : item.paymentProvider === "giftcard"
                                  ? "Gift Card"
                                  : item.paymentProvider === "paypal"
                                    ? "Card (Paypal)"
                                    : "Free Trial"}
                            </TableCell>
                            <TableCell>
                              {uppercaseFirst(item.status)}
                              {item.failureReason
                                ? ` - ${item.failureReason}`
                                : ""}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
}
